/**
 * BoostCard
 *
 * Single boost-level card inside BoostSection. Renders the boost name, the list
 * of benefits with thin separators, the +/- option duration selector, the
 * coin price for the selected option and the Select / Selected button.
 * Mirrors Flutter `_boostLevelItem` from content_monetization_screen.dart.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MathUtils } from '@/domain/utils/mathUtils';
import type { BoostEntity } from '@/domain/entities/boost/boostEntity';

interface BoostCardProps {
  boost: BoostEntity;
  isSelected: boolean;
  isInteractive: boolean;
  onSelect: () => void;
  onCycleOption: (forward: boolean) => void;
}

export const BoostCard: React.FC<BoostCardProps> = ({
  boost,
  isSelected,
  isInteractive,
  onSelect,
  onCycleOption,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const selectedOption = boost.options.find((o) => o.isSelected) ?? boost.options[0];
  const priceText = l10n.publish.boostPriceCoins.replace(
    '{coins}',
    MathUtils.removeTrailingZero(MathUtils.coinsFromUSD(selectedOption.price))
  );
  const buttonLabel = isSelected ? l10n.publish.boostSelected : l10n.publish.boostSelect;

  return (
    <div
      {...qaAttr({
        id: `boost-card-${boost.id}`,
        ...(isSelected ? { value: 'selected' } : {}),
      })}
      style={{
        backgroundColor: theme.secondary,
        borderRadius: responsive.borderRadius.md,
        padding: `${responsive.spacing.sm}px ${responsive.spacing['3xl']}px ${responsive.spacing.lg}px`,
        pointerEvents: isInteractive ? 'auto' : 'none',
      }}
    >
      {/* Boost name */}
      <div
        style={{
          textAlign: 'center',
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.bold,
          color: theme.text.primary,
          marginTop: responsive.spacing.sm,
          marginBottom: responsive.spacing['3xl'],
        }}
      >
        {boost.name}
      </div>

      {/* Benefits with separators */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {boost.benefits.map((benefit) => (
          <React.Fragment key={benefit}>
            <div
              style={{
                textAlign: 'center',
                fontSize: responsive.font.base,
                color: theme.text.primary,
              }}
            >
              {benefit}
            </div>
            <div
              style={{
                width: '100%',
                height: responsive.divider.small,
                backgroundColor: theme.separator,
                marginTop: responsive.spacing.lg,
                marginBottom: responsive.spacing.lg,
              }}
            />
          </React.Fragment>
        ))}
      </div>

      {/* +/- option duration selector */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: responsive.spacing.sm,
        }}
      >
        <button
          onClick={() => onCycleOption(false)}
          disabled={!isInteractive}
          style={{
            background: 'none',
            border: 'none',
            cursor: isInteractive ? 'pointer' : 'default',
            padding: 0,
            display: 'flex',
            justifyContent: 'flex-start',
            flex: 1,
          }}
        >
          <AssetIcon
            path="/assets/images/substract.png"
            width={responsive.action.imageSize / 2}
            height={responsive.action.imageSize / 2}
            color={theme.white}
          />
        </button>
        <div
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {selectedOption.durationDisplay}
        </div>
        <button
          onClick={() => onCycleOption(true)}
          disabled={!isInteractive}
          style={{
            background: 'none',
            border: 'none',
            cursor: isInteractive ? 'pointer' : 'default',
            padding: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            flex: 1,
          }}
        >
          <AssetIcon
            path="/assets/images/add.png"
            width={responsive.action.imageSize / 2}
            height={responsive.action.imageSize / 2}
            color={theme.white}
          />
        </button>
      </div>

      {/* Separator */}
      <div
        style={{
          width: '100%',
          height: responsive.divider.small,
          backgroundColor: theme.separator,
          marginTop: responsive.spacing.lg,
          marginBottom: responsive.spacing.lg,
        }}
      />

      {/* Price */}
      <div
        style={{
          textAlign: 'center',
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.bold,
          color: theme.text.primary,
          marginTop: responsive.spacing['2xl'],
          marginBottom: responsive.spacing.xs,
        }}
      >
        {priceText}
      </div>

      {/* Select / Selected button */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <ExpauseButton
          {...qaAttr({ id: `boost-card-${boost.id}-select` })}
          variant={isSelected ? ButtonVariant.primary : ButtonVariant.secondary}
          backgroundColor={isSelected ? theme.exclusive : theme.surface}
          onClick={onSelect}
          disabled={!isInteractive}
        >
          {buttonLabel}
        </ExpauseButton>
      </div>
    </div>
  );
};
